import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Grid3x3, Save, PackageCheck, Plus, RefreshCw, ArrowLeft, Layers, Trash2, Search, Copy, Sparkles, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

function heatColor(slot) {
  if (!slot.product_id) return 'bg-slate-100 border-slate-300 hover:bg-slate-200';
  const par = slot.par_level || 0;
  const cur = slot.current_qty || 0;
  if (par === 0) return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
  const ratio = cur / par;
  if (ratio <= 0.3) return 'bg-rose-100 border-rose-300 hover:bg-rose-200 text-rose-900';
  if (ratio <= 0.6) return 'bg-amber-100 border-amber-300 hover:bg-amber-200 text-amber-900';
  return 'bg-emerald-100 border-emerald-300 hover:bg-emerald-200 text-emerald-900';
}

export default function VendingPlanogramEditorPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [machines, setMachines] = useState([]);
  const [products, setProducts] = useState([]);
  const [machineId, setMachineId] = useState(params.get('machine_id') || '');
  const [rows, setRows] = useState(6);
  const [cols, setCols] = useState(8);
  const [slots, setSlots] = useState({}); // key "r-c" -> slot obj
  const [selected, setSelected] = useState(null); // "r-c"
  const [metrics, setMetrics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showTemplateSave, setShowTemplateSave] = useState(false);
  const [tplName, setTplName] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNarrative, setAiNarrative] = useState(null);

  const loadMachines = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/machines`);
      setMachines(data.items || []);
    } catch (e) { toast.error('Failed to load machines'); }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/products`);
      setProducts(data.items || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/vending/planogram-templates`);
      setTemplates(data.items || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadPlanogram = useCallback(async (mid) => {
    if (!mid) return;
    try {
      const { data } = await axios.get(`${API}/api/vending/planogram/${mid}`);
      setRows(data.rows || 6);
      setCols(data.columns || 8);
      setMetrics(data.metrics);
      const map = {};
      (data.slots || []).forEach(s => {
        map[`${s.row}-${s.column}`] = s;
      });
      setSlots(map);
    } catch (e) { toast.error('Failed to load planogram'); }
  }, []);

  useEffect(() => { loadMachines(); loadProducts(); loadTemplates(); }, [loadMachines, loadProducts, loadTemplates]);
  useEffect(() => { if (machineId) loadPlanogram(machineId); }, [machineId, loadPlanogram]);

  const key = (r, c) => `${r}-${c}`;
  const getSlot = (r, c) => slots[key(r, c)] || { slot_code: '', row: r, column: c, product_id: '', par_level: 0, current_qty: 0, price: 0, capacity: 0 };

  const upsertSlot = (r, c, patch) => {
    setSlots(prev => ({
      ...prev,
      [key(r, c)]: {
        ...getSlot(r, c),
        ...patch,
        row: r, column: c,
        slot_code: patch.slot_code || getSlot(r, c).slot_code || `${String.fromCharCode(65 + r)}${c + 1}`,
      },
    }));
  };

  const clearSlot = (r, c) => {
    setSlots(prev => {
      const copy = { ...prev };
      delete copy[key(r, c)];
      return copy;
    });
  };

  const assignProduct = (product) => {
    if (!selected) { toast.error('Pick a slot first'); return; }
    const [r, c] = selected.split('-').map(Number);
    upsertSlot(r, c, {
      product_id: product.id,
      price: product.price || 0,
      par_level: product.par_level || 10,
      capacity: product.capacity || 12,
      current_qty: 0,
    });
  };

  const saveGrid = async () => {
    if (!machineId) { toast.error('Pick a machine'); return; }
    const slotList = Object.values(slots).map(s => ({
      slot_code: s.slot_code || `${String.fromCharCode(65 + s.row)}${s.column + 1}`,
      row: s.row,
      column: s.column,
      product_id: s.product_id || null,
      par_level: s.par_level || 0,
      current_qty: s.current_qty || 0,
      price: s.price || 0,
      capacity: s.capacity || 0,
    }));
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/vending/planogram/apply`, {
        machine_id: machineId,
        rows,
        columns: cols,
        slots: slotList,
      });
      toast.success(`Planogram saved — ${data.slots_written} slots written`);
      loadPlanogram(machineId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) { toast.error('Name your template'); return; }
    const slotList = Object.values(slots).map(s => ({
      slot_code: s.slot_code, row: s.row, column: s.column,
      product_id: s.product_id || null, par_level: s.par_level || 0,
      current_qty: 0, price: s.price || 0, capacity: s.capacity || 0,
    }));
    try {
      const { data } = await axios.post(`${API}/api/vending/planogram/apply`, {
        machine_id: machineId, rows, columns: cols, slots: slotList,
        save_as_template: true, template_name: tplName,
      });
      if (data.template_id) toast.success('Template saved');
      setShowTemplateSave(false); setTplName('');
      loadTemplates();
    } catch (e) { toast.error('Template save failed'); }
  };

  const applyTemplate = async (tpl) => {
    if (!machineId) { toast.error('Pick a machine first'); return; }
    try {
      await axios.post(`${API}/api/vending/planogram/${machineId}/apply-template/${tpl.id}`);
      toast.success(`Applied "${tpl.name}"`);
      loadPlanogram(machineId);
    } catch (e) { toast.error('Failed to apply template'); }
  };

  const aiSuggest = async (useLlm = false) => {
    if (!machineId) { toast.error('Pick a machine first'); return; }
    if (useLlm) {
      if (!window.confirm('Ask Kaharian Gateway AI to reason over the suggestion? This uses your credits.')) return;
    }
    setAiLoading(true);
    setAiNarrative(null);
    try {
      const { data } = await axios.post(`${API}/api/vending/planogram/ai-suggest`, {
        machine_id: machineId,
        use_llm: useLlm,
        lookback_days: 30,
      });
      const map = {};
      (data.slots || []).forEach(s => { map[`${s.row}-${s.column}`] = s; });
      setSlots(map);
      setRows(data.rows);
      setCols(data.columns);
      if (data.narrative) setAiNarrative(data.narrative);
      toast.success(`AI suggestion ready — ${data.slots.length} slots planned${data.llm_used ? ' (with AI reasoning)' : ''}${data.llm_error ? ` · fallback used: ${data.llm_error.slice(0, 60)}` : ''}. Review then Save.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggestion failed');
    } finally { setAiLoading(false); }
  };

  const selectedSlotObj = selected ? getSlot(...selected.split('-').map(Number)) : null;
  const filteredProducts = productSearch
    ? products.filter(p => (p.name || '').toLowerCase().includes(productSearch.toLowerCase()) || (p.sku || '').toLowerCase().includes(productSearch.toLowerCase()))
    : products;

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="planogram-editor-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => navigate('/vending')} data-testid="planogram-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Fleet
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">Vending Fleet</div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><Grid3x3 className="h-6 w-6 text-emerald-600" /> Planogram Editor</h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={machineId} onChange={(e) => setMachineId(e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white" data-testid="planogram-machine-select">
            <option value="">Pick machine…</option>
            {machines.map(m => <option key={m.id} value={m.id}>{m.chassis_serial || m.name || m.id.slice(0, 8)}</option>)}
          </select>
          <Input type="number" value={rows} onChange={(e) => setRows(Math.max(1, Math.min(20, parseInt(e.target.value || '1'))))} className="w-16 h-9" data-testid="planogram-rows" title="Rows" />
          <span className="text-gray-400">×</span>
          <Input type="number" value={cols} onChange={(e) => setCols(Math.max(1, Math.min(20, parseInt(e.target.value || '1'))))} className="w-16 h-9" data-testid="planogram-cols" title="Columns" />
          <Button size="sm" variant="outline" onClick={() => loadPlanogram(machineId)} disabled={!machineId} data-testid="planogram-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => aiSuggest(false)} disabled={!machineId || aiLoading} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="planogram-ai-suggest">
            {aiLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI Suggest
          </Button>
          <Button size="sm" variant="outline" onClick={() => aiSuggest(true)} disabled={!machineId || aiLoading} className="border-purple-400 text-purple-800 hover:bg-purple-50" data-testid="planogram-ai-suggest-llm">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> AI + Reasoning
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowTemplateSave(true)} disabled={!machineId || Object.keys(slots).length === 0} data-testid="planogram-save-template">
            <Copy className="h-3.5 w-3.5 mr-1" /> Save Template
          </Button>
          <Button size="sm" onClick={saveGrid} disabled={!machineId || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="planogram-save">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          <MetricCard label="Slots" value={metrics.total_slots} />
          <MetricCard label="Empty" value={metrics.empty_slots} tone={metrics.empty_slots > 0 ? 'warn' : 'ok'} />
          <MetricCard label="Low Stock" value={metrics.low_stock_slots} tone={metrics.low_stock_slots > 0 ? 'danger' : 'ok'} />
          <MetricCard label="Capacity" value={metrics.total_capacity} />
          <MetricCard label="Fill Ratio" value={`${Math.round((metrics.fill_ratio || 0) * 100)}%`} tone={metrics.fill_ratio > 0.7 ? 'ok' : 'warn'} />
        </div>
      )}

      {aiNarrative && (
        <div className="mb-4 p-3 rounded-lg border border-purple-200 bg-purple-50" data-testid="planogram-ai-narrative">
          <div className="flex items-center gap-2 text-xs font-semibold text-purple-900 mb-1"><Sparkles className="h-3.5 w-3.5" /> AI Reasoning (via Kaharian Gateway)</div>
          <p className="text-xs text-purple-900 leading-relaxed whitespace-pre-wrap">{aiNarrative}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Layers className="h-4 w-4" /> Machine Grid</CardTitle></CardHeader>
          <CardContent className="p-3 overflow-auto" data-testid="planogram-grid">
            {!machineId && <p className="text-sm text-gray-medium py-8 text-center">Pick a machine to load its planogram.</p>}
            {machineId && (
              <div className="inline-block min-w-full">
                <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(72px, 1fr))` }}>
                  {Array.from({ length: rows * cols }).map((_, i) => {
                    const r = Math.floor(i / cols);
                    const c = i % cols;
                    const s = getSlot(r, c);
                    const k = key(r, c);
                    const label = s.slot_code || `${String.fromCharCode(65 + r)}${c + 1}`;
                    const active = selected === k;
                    const product = products.find(p => p.id === s.product_id);
                    return (
                      <button
                        key={k}
                        onClick={() => setSelected(k)}
                        data-testid={`planogram-slot-${label}`}
                        className={`border-2 rounded-lg p-2 text-left transition-all ${heatColor(s)} ${active ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`}
                      >
                        <div className="text-[10px] font-bold opacity-70">{label}</div>
                        {s.product_id ? (
                          <>
                            <div className="text-[11px] font-medium truncate">{product?.name || 'Product'}</div>
                            <div className="text-[9px] opacity-70">{s.current_qty}/{s.par_level}</div>
                          </>
                        ) : (
                          <div className="text-[9px] text-gray-400 italic">empty</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3">
          {/* Slot inspector */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Slot Detail</CardTitle></CardHeader>
            <CardContent className="p-3 space-y-2">
              {selected ? (
                <>
                  <div className="text-xs text-gray-medium">Slot <b>{selectedSlotObj?.slot_code || selected}</b></div>
                  <label className="text-[11px] text-gray-medium">Slot code</label>
                  <Input value={selectedSlotObj?.slot_code || ''} onChange={(e) => { const [r, c] = selected.split('-').map(Number); upsertSlot(r, c, { slot_code: e.target.value }); }} className="h-8 text-xs" data-testid="planogram-slot-code" />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-gray-medium">Par</label>
                      <Input type="number" value={selectedSlotObj?.par_level || 0} onChange={(e) => { const [r, c] = selected.split('-').map(Number); upsertSlot(r, c, { par_level: parseInt(e.target.value || '0') }); }} className="h-8 text-xs" data-testid="planogram-slot-par" />
                    </div>
                    <div>
                      <label className="text-[11px] text-gray-medium">Current</label>
                      <Input type="number" value={selectedSlotObj?.current_qty || 0} onChange={(e) => { const [r, c] = selected.split('-').map(Number); upsertSlot(r, c, { current_qty: parseInt(e.target.value || '0') }); }} className="h-8 text-xs" data-testid="planogram-slot-qty" />
                    </div>
                    <div>
                      <label className="text-[11px] text-gray-medium">Capacity</label>
                      <Input type="number" value={selectedSlotObj?.capacity || 0} onChange={(e) => { const [r, c] = selected.split('-').map(Number); upsertSlot(r, c, { capacity: parseInt(e.target.value || '0') }); }} className="h-8 text-xs" data-testid="planogram-slot-cap" />
                    </div>
                    <div>
                      <label className="text-[11px] text-gray-medium">Price</label>
                      <Input type="number" step="0.01" value={selectedSlotObj?.price || 0} onChange={(e) => { const [r, c] = selected.split('-').map(Number); upsertSlot(r, c, { price: parseFloat(e.target.value || '0') }); }} className="h-8 text-xs" data-testid="planogram-slot-price" />
                    </div>
                  </div>
                  <div className="text-xs text-gray-medium pt-1">
                    Product: {selectedSlotObj?.product_id ? (products.find(p => p.id === selectedSlotObj.product_id)?.name || 'Unknown') : <span className="italic">none — pick below</span>}
                  </div>
                  <Button size="sm" variant="outline" className="w-full h-8 text-xs text-rose-600 border-rose-200" onClick={() => { const [r, c] = selected.split('-').map(Number); clearSlot(r, c); setSelected(null); }} data-testid="planogram-slot-clear">
                    <Trash2 className="h-3 w-3 mr-1" /> Clear Slot
                  </Button>
                </>
              ) : (
                <p className="text-xs text-gray-medium text-center py-6">Click a slot on the grid to edit it.</p>
              )}
            </CardContent>
          </Card>

          {/* Product palette */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><PackageCheck className="h-4 w-4" /> Assign Product</CardTitle></CardHeader>
            <CardContent className="p-3">
              <div className="relative mb-2">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                <Input placeholder="Search products…" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="h-8 pl-8 text-xs" data-testid="planogram-product-search" />
              </div>
              <div className="space-y-1 max-h-[300px] overflow-auto">
                {filteredProducts.length === 0 && <p className="text-[11px] text-gray-medium italic text-center py-3">No products — add them in Vending → Products first.</p>}
                {filteredProducts.map(p => (
                  <button key={p.id} onClick={() => assignProduct(p)} disabled={!selected} data-testid={`planogram-product-${p.id}`} className="w-full text-left p-2 rounded border border-gray-200 hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="text-xs font-medium truncate">{p.name}</div>
                    <div className="text-[10px] text-gray-medium">{p.sku || '—'} · ${p.price?.toFixed(2) || '0.00'}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Templates */}
          {templates.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Templates</CardTitle></CardHeader>
              <CardContent className="p-3 space-y-1 max-h-[200px] overflow-auto">
                {templates.map(t => (
                  <button key={t.id} onClick={() => applyTemplate(t)} className="w-full text-left p-2 rounded border border-gray-200 hover:bg-emerald-50" data-testid={`planogram-template-${t.id}`}>
                    <div className="text-xs font-medium truncate">{t.name}</div>
                    <div className="text-[10px] text-gray-medium">{t.rows}×{t.columns} · {t.slots?.length || 0} slots</div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showTemplateSave} onOpenChange={setShowTemplateSave}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save as Template</DialogTitle></DialogHeader>
          <Input placeholder="Template name" value={tplName} onChange={(e) => setTplName(e.target.value)} data-testid="planogram-template-name" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateSave(false)}>Cancel</Button>
            <Button onClick={saveTemplate} className="bg-emerald-600" data-testid="planogram-template-save-confirm">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value, tone = 'ok' }) {
  const toneMap = {
    ok: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    warn: 'bg-amber-50 border-amber-200 text-amber-700',
    danger: 'bg-rose-50 border-rose-200 text-rose-700',
  };
  return (
    <div className={`p-2 rounded-lg border ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}
